import { Link } from "react-router-dom";
import { Facebook, Instagram, Linkedin } from "lucide-react";
import { ORGANIZATION_SAME_AS } from "@/lib/seo";

const socialFooterLinks = [
  { href: ORGANIZATION_SAME_AS[0], label: "Instagram", Icon: Instagram },
  { href: ORGANIZATION_SAME_AS[1], label: "LinkedIn", Icon: Linkedin },
  { href: ORGANIZATION_SAME_AS[2], label: "Facebook", Icon: Facebook },
] as const;

const footerLinkGroups = [
  {
    title: "Produto",
    links: [
      { to: "/#features", label: "Recursos" },
      { to: "/whatsapp/disparos", label: "Disparos" },
      { to: "/recuperacao-de-leads", label: "Recuperação de leads" },
      { to: "/catalogo-de-produtos", label: "Catálogo" },
      { to: "/#pricing", label: "Preços" },
      { to: "/sobre", label: "Sobre" },
    ],
  },
  {
    title: "Canais",
    links: [
      { to: "/whatsapp/agente-ia", label: "WhatsApp" },
      { to: "/instagram/agente-ia", label: "Instagram" },
      { to: "/integracoes/crm", label: "CRM integrado" },
      { to: "/negociacoes", label: "Negociações" },
      { to: "/whatsapp/chatbot", label: "Chatbot" },
    ],
  },
  {
    title: "Setores",
    links: [
      { to: "/setores/imobiliarias", label: "Imobiliárias" },
      { to: "/setores/clinicas", label: "Clínicas" },
      { to: "/setores/educacao", label: "Educação" },
      { to: "/setores/servicos", label: "SaaS e serviços" },
    ],
  },
  {
    title: "Suporte",
    links: [
      { to: "/#faq", label: "FAQ" },
      { to: "/llm", label: "Resumo FabChat para IA" },
      { to: "/contato", label: "Contato" },
    ],
  },
  {
    title: "Legal",
    links: [
      { to: "/privacidade", label: "Privacidade" },
      { to: "/termos", label: "Termos" },
      { to: "/cookies", label: "Cookies" },
      { to: "/seguranca-lgpd", label: "LGPD" },
    ],
  },
] as const;

function FooterLinkGroup({
  title,
  links,
}: {
  title: string;
  links: readonly { to: string; label: string }[];
}) {
  return (
    <div className="min-w-0">
      <h3 className="text-[11px] font-medium uppercase tracking-wider text-foreground/70 mb-2.5">
        {title}
      </h3>
      <ul className="flex flex-col gap-1.5 list-none p-0 m-0">
        {links.map(({ to, label }) => (
          <li key={to}>
            <Link
              to={to}
              className="text-[13px] text-muted-foreground hover:text-foreground transition-colors leading-snug"
            >
              {label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export const Footer = () => {
  return (
    <footer className="dark border-t border-white/10 bg-[hsl(240_35%_8%)]">
      <div className="mx-auto max-w-6xl px-4 py-8 md:py-10">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-x-8 lg:gap-y-0 lg:items-start">
          {/* Marca */}
          <div className="flex flex-col items-center text-center lg:col-span-4 lg:items-start lg:text-left">
            <Link to="/" className="inline-flex mb-3">
              <img
                src="/logo-light.png?v=4"
                alt="FabChat"
                width={160}
                height={40}
                className="h-6 w-auto object-contain dark:hidden"
              />
              <img
                src="/logo.png?v=4"
                alt="FabChat"
                width={160}
                height={40}
                className="h-6 w-auto object-contain hidden dark:block"
              />
            </Link>
            <p className="text-xs text-muted-foreground leading-relaxed max-w-[280px] lg:max-w-none">
              Agentes de IA para WhatsApp e Instagram. Automatize vendas, suporte e atendimento.
            </p>
            <div
              className="flex items-center justify-center gap-2 mt-4 lg:justify-start"
              role="navigation"
              aria-label="Redes sociais"
            >
              {socialFooterLinks.map(({ href, label, Icon }) => (
                <a
                  key={href}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${label} — FabTech (abre em nova aba)`}
                  className="text-muted-foreground hover:text-foreground transition-colors rounded-md p-1.5 hover:bg-white/5"
                >
                  <Icon className="h-4 w-4" aria-hidden />
                </a>
              ))}
            </div>
          </div>

          {/* Links */}
          <nav
            aria-label="Rodapé"
            className="lg:col-span-8 grid grid-cols-2 gap-x-6 gap-y-8 sm:grid-cols-3 md:grid-cols-5 md:gap-x-4"
          >
            {footerLinkGroups.map((group) => (
              <FooterLinkGroup key={group.title} title={group.title} links={group.links} />
            ))}
          </nav>
        </div>

        <div className="mt-8 pt-6 border-t border-white/10 text-center text-[11px] text-muted-foreground/80 leading-relaxed">
          <p>&copy; 2025 FabChat. Todos os direitos reservados.</p>
          <p className="mt-1">CNPJ: 64.052.610/0001-11</p>
        </div>
      </div>
    </footer>
  );
};
